import axios from "axios";

/**
 * Where the API lives.
 *
 * Pinned to the deployed backend on purpose, so a page opened anywhere talks
 * to the same server. To work against a local API, put the line back:
 *   export const API_BASE = process.env.REACT_APP_API_URL || "http://localhost:5000/api";
 */
export const API_BASE = "https://appglt.com/api";
// "http://localhost:5000/api" ||
const client = axios.create({ baseURL: API_BASE, timeout: 30000 });

/* Attach the bearer token to every request. */
client.interceptors.request.use((config) => {
  const token = localStorage.getItem("gl_token");
  if (token) config.headers.Authorization = `Bearer ${token}`;
  // The server builds QR codes and share links from this, so they point at the
  // domain actually in use — a Vercel preview, the real domain, or localhost.
  config.headers["X-Client-Origin"] = window.location.origin;
  return config;
});

/**
 * Unwraps the server's { success, message, data, meta } envelope and turns
 * failures into a rejected promise carrying a readable message, so callers
 * only ever handle `err.message`.
 */
client.interceptors.response.use(
  (res) => res,
  (error) => {
    if (error.response?.status === 401 && localStorage.getItem("gl_token")) {
      localStorage.removeItem("gl_token");
      localStorage.removeItem("gl_user");
      // Avoid a redirect loop when the login request itself 401s.
      if (!window.location.pathname.startsWith("/login"))
        window.location.href = "/login";
    }
    const message =
      error.response?.data?.message ||
      (error.code === "ECONNABORTED"
        ? "The server took too long to respond"
        : null) ||
      (!error.response
        ? "Cannot reach the server. Is it running?"
        : "Something went wrong");
    return Promise.reject(new Error(message));
  }
);

/** GET → returns `data`; pass `withMeta` when pagination is needed. */
export async function get(url, params = {}, withMeta = false) {
  const res = await client.get(url, { params });
  return withMeta
    ? { data: res.data.data, meta: res.data.meta }
    : res.data.data;
}

export async function post(url, body, config) {
  const res = await client.post(url, body, config);
  return res.data;
}

export async function put(url, body) {
  const res = await client.put(url, body);
  return res.data;
}

export async function patch(url, body) {
  const res = await client.patch(url, body);
  return res.data;
}

export async function del(url) {
  const res = await client.delete(url);
  return res.data;
}

/** Multipart helper for document / ticket / photo uploads. */
/** POSTs a pre-built FormData — used where field names are dynamic. */
export async function postForm(url, formData) {
  const res = await client.post(url, formData, {
    headers: { "Content-Type": "multipart/form-data" },
  });
  return res.data;
}

export async function upload(
  url,
  file,
  fields = {},
  fieldName = "file",
  config = {}
) {
  const form = new FormData();
  if (Array.isArray(file)) file.forEach((f) => form.append("files", f));
  else form.append(fieldName, file);
  Object.entries(fields).forEach(
    ([k, v]) => v !== undefined && form.append(k, v)
  );

  // `config` is where a slow upload asks for more than the default timeout —
  // reading a long spreadsheet takes longer than an ordinary request.
  const res = await client.post(url, form, {
    ...config,
    headers: {
      "Content-Type": "multipart/form-data",
      ...(config.headers || {}),
    },
  });
  return res.data;
}

/** Triggers a browser download for the Excel report endpoints. */
export async function download(url, params = {}, filename = "report.xlsx") {
  const res = await client.get(url, { params, responseType: "blob" });
  const href = window.URL.createObjectURL(new Blob([res.data]));
  const a = document.createElement("a");
  a.href = href;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.URL.revokeObjectURL(href);
}

export default client;
